package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue

enum class MeshBackground(val color: Int) {
    DARK(0x0f0f18),
    LIGHT(0xeef2f7),
    GREY(0x4b5563),
    /** Roblox sky with the grey baseplate under the model. */
    BASEPLATE(0x8fc1ee)
}

/** Camera of a model viewer; two viewers given the same state move together (version comparison). */
class MeshCameraState(rotX: Double = 0.38, rotY: Double = 2.42, zoom: Double = 6.2) {
    val rotX = mutableStateOf(rotX)
    val rotY = mutableStateOf(rotY)
    val zoom = mutableStateOf(zoom)
    val targetX = mutableStateOf(0.0)
    val targetY = mutableStateOf(0.0)
    val targetZ = mutableStateOf(0.0)
}

/** How the model is shown; shared between the viewer and the panel under it. */
class MeshViewState {
    var shading by mutableStateOf(MeshShadingMode.TEXTURED)
    var lighting by mutableStateOf(MeshLightingPreset.STUDIO)
    var background by mutableStateOf(MeshBackground.DARK)
    var showGrid by mutableStateOf(true)
    var showReference by mutableStateOf(false)
    var showBounds by mutableStateOf(false)
    var autoRotate by mutableStateOf(false)
    /** Normal map on: the surface relief baked from the detailed model. */
    var showRelief by mutableStateOf(true)
    /** Filled by the viewer once the model is loaded. */
    var stats by mutableStateOf(MeshModelStats())
    /** Each increment asks the viewer for a PNG; transparent leaves out the background, floor and helpers. */
    var screenshotRequests by mutableStateOf(0)
    var screenshotTransparent by mutableStateOf(false)
    /** Each increment records one full turn of the model as a short video. */
    var videoRequests by mutableStateOf(0)
    var recording by mutableStateOf(false)
}

/** Height of the reference manikin's geometry (feet to top of head) before scaling to an R15 avatar. */
internal const val REFERENCE_RIG_HEIGHT = 5.805

/**
 * three.js helpers for the model viewer, written as plain JS functions that take everything as arguments
 * (Kotlin locals must not be referenced from js() strings: the compiler renames them).
 */
internal val MeshSceneJs: dynamic = js(
    """({
    checkerTexture: function() {
        var T = window.THREE;
        if (window.__rcChecker) return window.__rcChecker;
        var c = document.createElement('canvas');
        c.width = c.height = 512;
        var g = c.getContext('2d');
        var n = 8, s = 512 / n;
        for (var y = 0; y < n; y++) {
            for (var x = 0; x < n; x++) {
                var dark = (x + y) % 2 === 1;
                g.fillStyle = 'hsl(' + (x * 45) + ', 70%, ' + (dark ? 32 : 68) + '%)';
                g.fillRect(x * s, y * s, s, s);
                g.fillStyle = dark ? '#ffffff' : '#111111';
                g.font = 'bold 20px sans-serif';
                g.fillText(String.fromCharCode(65 + x) + (y + 1), x * s + 8, y * s + 26);
            }
        }
        var t = new T.CanvasTexture(c);
        t.wrapS = t.wrapT = T.RepeatWrapping;
        window.__rcChecker = t;
        return t;
    },

    shade: function(root, mode) {
        var T = window.THREE;
        if (!T || !root) return;
        var self = this;
        root.traverse(function(c) {
            if (!c || !c.isMesh || !c.material) return;
            if (!c.__originalMaterial) {
                c.__originalMaterial = c.material;
                // Files without normals light up black; Studio computes them on import, so do the same.
                if (c.geometry && !c.geometry.attributes.normal) c.geometry.computeVertexNormals();
                // glTF's default metalness is 1, and generators often leave it unset: without an environment map
                // such a material renders black. Roblox shows MeshPart textures without metal, so do the same.
                (Array.isArray(c.material) ? c.material : [c.material]).forEach(function(m) {
                    if (m && m.metalness !== undefined && !m.metalnessMap) { m.metalness = 0; m.needsUpdate = true; }
                });
            }
            var orig = c.__originalMaterial;
            var first = Array.isArray(orig) ? orig[0] : orig;
            function each(fn) { (Array.isArray(orig) ? orig : [orig]).forEach(fn); }
            function cached(key, make) { if (!c[key]) c[key] = make(); return c[key]; }
            if (mode === 'TEXTURED' || mode === 'WIREFRAME_OVERLAY' || mode === 'FLAT') {
                each(function(m) {
                    m.wireframe = mode === 'WIREFRAME_OVERLAY';
                    m.flatShading = mode === 'FLAT';
                    m.needsUpdate = true;
                });
                c.material = orig;
            } else if (mode === 'CLAY') {
                c.material = cached('__clay', function() {
                    return new T.MeshStandardMaterial({ color: 0xd1d5db, roughness: 0.65, metalness: 0.08 });
                });
            } else if (mode === 'NORMALS') {
                c.material = cached('__normals', function() { return new T.MeshNormalMaterial(); });
            } else if (mode === 'UV_CHECKER') {
                c.material = cached('__checker', function() {
                    return new T.MeshStandardMaterial({ map: self.checkerTexture(), roughness: 0.8, metalness: 0 });
                });
            } else if (mode === 'BASE_COLOR') {
                c.material = cached('__base', function() {
                    return new T.MeshBasicMaterial({ map: first && first.map ? first.map : null, color: first && first.color ? first.color.clone() : 0xffffff });
                });
            } else if (mode === 'XRAY') {
                c.material = cached('__xray', function() {
                    return new T.MeshBasicMaterial({ color: 0x60a5fa, transparent: true, opacity: 0.22, depthWrite: false, side: T.DoubleSide });
                });
            }
        });
    },

    relief: function(root, enabled) {
        if (!root) return false;
        var found = false;
        root.traverse(function(c) {
            if (!c || !c.isMesh) return;
            var orig = c.__originalMaterial || c.material;
            (Array.isArray(orig) ? orig : [orig]).forEach(function(m) {
                if (!m) return;
                if (m.__normalMap === undefined) m.__normalMap = m.normalMap || null;
                if (m.__normalMap) {
                    found = true;
                    m.normalMap = enabled ? m.__normalMap : null;
                    m.needsUpdate = true;
                }
            });
        });
        return found;
    },

    textureSize: function(root) {
        var best = 0;
        if (!root) return 0;
        root.traverse(function(c) {
            if (!c || !c.isMesh || !c.material) return;
            var mats = Array.isArray(c.material) ? c.material : [c.material];
            mats.forEach(function(m) {
                var img = m && m.map && m.map.image;
                if (img) best = Math.max(best, img.width || 0, img.height || 0);
            });
        });
        return best;
    },

    setBackground: function(scene, renderer, hex) {
        scene.background = new window.THREE.Color(hex);
        renderer.setClearColor(hex, 1);
    },

    baseplate: function(scene, y, scale, visible) {
        var T = window.THREE;
        if (!scene.__rcBaseplate) {
            var c = document.createElement('canvas');
            c.width = c.height = 64;
            var g = c.getContext('2d');
            g.fillStyle = '#63666b';
            g.fillRect(0, 0, 64, 64);
            g.strokeStyle = '#575a5f';
            g.lineWidth = 3;
            g.strokeRect(1.5, 1.5, 61, 61);
            var t = new T.CanvasTexture(c);
            t.wrapS = t.wrapT = T.RepeatWrapping;
            var plane = new T.Mesh(new T.PlaneGeometry(1, 1), new T.MeshStandardMaterial({ map: t, roughness: 0.9 }));
            plane.rotation.x = -Math.PI / 2;
            scene.add(plane);
            scene.__rcBaseplate = plane;
        }
        var p = scene.__rcBaseplate;
        var size = 64;
        p.scale.set(size, size, 1);
        p.position.y = y - 0.002;
        // One tile per 4 studs, like the Roblox baseplate.
        p.material.map.repeat.set(size / (4 * scale), size / (4 * scale));
        p.visible = visible;
    },

    referenceRig: function(scene, scale, x, groundY, visible) {
        var T = window.THREE;
        if (!scene.__rcReference) {
            var shirt = new T.MeshStandardMaterial({ color: 0x3b82f6, roughness: 0.6 });
            var skin = new T.MeshStandardMaterial({ color: 0xf5d0a9, roughness: 0.6 });
            var pants = new T.MeshStandardMaterial({ color: 0x1f2937, roughness: 0.7 });
            var group = new T.Group();
            function box(w, h, d, px, py, pz, mat) {
                var m = new T.Mesh(new T.BoxGeometry(w, h, d), mat);
                m.position.set(px, py, pz);
                group.add(m);
            }
            // Feet at y = 0; same proportions as the animation manikin (REFERENCE_RIG_HEIGHT tall).
            var hip = 2.555;
            box(2.0, 0.4, 1.0, 0, hip + 0.2, 0, pants);
            box(2.0, 1.6, 1.0, 0, hip + 1.2, 0, shirt);
            box(1.2, 1.2, 1.2, 0, hip + 2.65, 0, skin);
            [-1, 1].forEach(function(sx) {
                box(0.9, 2.55, 0.9, 1.5 * sx, hip + 0.575, 0, shirt);
                box(0.95, 2.2, 0.95, 0.5 * sx, hip - 1.1, 0, pants);
                box(0.95, 0.35, 1.25, 0.5 * sx, 0.175, -0.12, pants);
            });
            scene.add(group);
            scene.__rcReference = group;
        }
        var r = scene.__rcReference;
        var s = scale * 5.2 / 5.805;
        r.scale.set(s, s, s);
        r.position.set(x, groundY, 0);
        r.visible = visible;
    },

    bounds: function(scene, model, visible, labels) {
        var T = window.THREE;
        if (scene.__rcBounds) {
            scene.remove(scene.__rcBounds);
            scene.__rcBounds = null;
        }
        if (!visible || !model) return;
        var group = new T.Group();
        // Two statements on purpose: Kotlin's js() parser turns "new A().b(x)" into "new (A().b)(x)".
        var box = new T.Box3();
        box.setFromObject(model);
        var size = box.getSize(new T.Vector3());
        var edges = new T.LineSegments(
            new T.EdgesGeometry(new T.BoxGeometry(size.x, size.y, size.z)),
            new T.LineBasicMaterial({ color: 0xfbbf24 })
        );
        box.getCenter(edges.position);
        group.add(edges);
        function label(text, x, y, z) {
            var c = document.createElement('canvas');
            c.width = 256; c.height = 64;
            var g = c.getContext('2d');
            g.fillStyle = 'rgba(15,15,24,0.85)';
            g.fillRect(0, 0, 256, 64);
            g.fillStyle = '#fbbf24';
            g.font = 'bold 34px sans-serif';
            g.textAlign = 'center';
            g.textBaseline = 'middle';
            g.fillText(text, 128, 34);
            var sprite = new T.Sprite(new T.SpriteMaterial({ map: new T.CanvasTexture(c), depthTest: false }));
            var h = Math.max(size.x, size.y, size.z) * 0.09;
            sprite.scale.set(h * 4, h, 1);
            sprite.position.set(x, y, z);
            sprite.renderOrder = 10;
            group.add(sprite);
        }
        var mn = box.min, mx = box.max;
        label(labels[0], (mn.x + mx.x) / 2, mn.y, mn.z);
        label(labels[1], mx.x, (mn.y + mx.y) / 2, mn.z);
        label(labels[2], mx.x, mn.y, (mn.z + mx.z) / 2);
        scene.add(group);
        scene.__rcBounds = group;
    },

    record: function(canvas, ms, done) {
        if (typeof MediaRecorder === 'undefined' || !canvas.captureStream) return false;
        var types = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4'];
        var type = null;
        for (var i = 0; i < types.length; i++) {
            if (MediaRecorder.isTypeSupported(types[i])) { type = types[i]; break; }
        }
        if (!type) return false;
        var recorder = new MediaRecorder(canvas.captureStream(30), { mimeType: type, videoBitsPerSecond: 6000000 });
        var chunks = [];
        recorder.ondataavailable = function(e) { if (e.data && e.data.size) chunks.push(e.data); };
        recorder.onstop = function() { done(new Blob(chunks, { type: type }), type.indexOf('mp4') >= 0 ? 'mp4' : 'webm'); };
        recorder.start();
        setTimeout(function() { recorder.stop(); }, ms);
        return true;
    },

    snapshot: function(renderer, scene, camera, transparent) {
        var hidden = [];
        var bg = scene.background;
        if (transparent) {
            scene.children.forEach(function(c) {
                if (c.visible && (c.isGridHelper || c.type === 'AxesHelper' || c === scene.__rcBaseplate || c === scene.__rcBounds)) {
                    c.visible = false;
                    hidden.push(c);
                }
            });
            scene.background = null;
            renderer.setClearColor(0x000000, 0);
        }
        renderer.render(scene, camera);
        var url = renderer.domElement.toDataURL('image/png');
        if (transparent) {
            hidden.forEach(function(c) { c.visible = true; });
            scene.background = bg;
            if (bg) renderer.setClearColor(bg, 1);
        }
        return url;
    }
})"""
)
